import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Utils from "../../Utils";

const RecceTab = ({ mode, psId, canUpload }) => {
  /* ✅ Hooks ALWAYS first */
  const fileInputRef = useRef(null);
  const [downloadable, setDownloadable] = useState(false);
  const [recceImages, setRecceImages] = useState([]);
  const [recLoading, setRecLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  /* ---------------- FETCH LIST ---------------- */
  const loadRecceImages = () => {
    axios
      .get(
        Utils.apiUrl(`photoshoot/recces/${psId}/${mode}`),
        Utils.apiHeaders(),
      )
      .then((res) => {
        if (res.data.success) {
          setRecceImages(res.data.data);
          setDownloadable(res.data.downloadable);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadRecceImages();
  }, [psId]);

  /* ---------------- FILE UPLOAD ---------------- */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      alert("Only PDF files allowed");
      return;
    }

    setRecLoading(true);

    const formData = new FormData();
    formData.append("pdffile", file);

    axios
      .post(
        Utils.apiUrl(`photoshoot/reccepdf/${psId}/${mode}`),
        formData,
        Utils.apiHeaders(),
      )
      .then((res) => {
        setRecLoading(false);
        alert(res.data.message);
        if (res.data.success) loadRecceImages();
      })
      .catch(() => {
        setRecLoading(false);
        alert("Upload failed");
      });
  };

  /* ---------------- DELETE ---------------- */
  const deleteRecce = (id) => {
    if (!window.confirm("Delete this file?")) return;

    axios
      .delete(Utils.apiUrl(`photoshoot/recce/${id}`), Utils.apiHeaders())
      .then((res) => {
        alert(res.data.message);
        if (res.data.success) loadRecceImages();
      });
  };

  /* ---------------- DRAG & DROP ---------------- */
  const stopEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    stopEvent(e);
    const file = e.dataTransfer.files?.[0];
    if (!file || file.type !== "application/pdf") return;

    fileInputRef.current.files = e.dataTransfer.files;
    fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const downloadPdf = () => {
    axios
      .get(
        Utils.apiUrl(`photoshoot/downloadPdf/${mode}/${psId}`),
        Utils.apiHeaders({
          responseType: "blob",
        }),
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `photoshoot_${psId}_${mode}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(() => {
        alert("Download failed");
      });
  };

  const title = mode === "recce" ? "Photoshoot Recce" : "Styling Deck";

  /* ---------------- UI ---------------- */
  return (
    <div id={`tab-${mode}`} className={`tab-panel active`}>
      {mode === "recce" && (
        <div className="instruction-note mt-4 mb-4">
          <i className="fas fa-lightbulb mr-2"></i>
          Required only for New Build and Conversion Hotel.
        </div>
      )}

      {recLoading && (
        <div className="text-blue-500 font-semibold py-4">Uploading…</div>
      )}

      {!recLoading && (
        <>
          {canUpload() && (
            <div
              className="drop-zone cursor-pointer"
              onClick={() => fileInputRef.current.click()}
              onDrop={handleDrop}
              onDragOver={stopEvent}
              onDragLeave={stopEvent}
            >
              <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
              <p>Drag & Drop PDF or click to upload</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}

          {!canUpload() && downloadable && (
            <div className="instruction-note mt-4 mb-4">
              <i className="fas fa-lightbulb mr-2"></i>
              You can download the whole annotation as{" "}
              <button
                className="btn rounded-md bg-yellow-100 p-1 pr-2 text-sm"
                onClick={downloadPdf}
              >
                <i className="fas fa-download mx-1"></i>
                PDF
              </button>
            </div>
          )}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            {recceImages.map((item, index) => (
              <div
                key={item.id}
                className="relative bg-white rounded-lg shadow border"
              >
                <div className="h-40 bg-gray-100 flex items-center justify-center">
                  <img
                    src={`${apiUrl}/photoshoot/recce/thumb/${item.fname}`}
                    className="object-cover w-full h-full"
                    alt=""
                  />
                </div>

                <div className="p-3 text-sm font-semibold truncate">
                  File {index + 1}
                </div>

                {item.status === "submitted" && (
                  <button
                    className="absolute top-2 right-2 text-red-700"
                    onClick={() => deleteRecce(item.id)}
                  >
                    <i className="fa-solid fa-x"></i>
                  </button>
                )}

                {["bt-processed"].includes(item.status) && (
                  <a
                    className="absolute top-2 right-2 bg-green-700 text-white p-1 rounded text-sm"
                    href={`${process.env.PUBLIC_URL}/photoshoot-recce/${psId}/${mode}/${item.fname}`}
                  >
                    <i className="fa-solid fa-magnifying-glass"></i>
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default RecceTab;
